import React from 'react';

export const TermsOfService: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 py-16">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service & Disclaimer</h1>

        <div className="prose prose-lg max-w-none text-gray-600 space-y-6">
          <p className="text-sm text-gray-500">Last Updated: June 1, 2026</p>

          {/* Disclaimer Section */}
          <section className="bg-amber-50 border-l-4 border-amber-500 p-6 my-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">⚠️ Important Disclaimer</h2>
            <p className="text-gray-700 font-medium mb-4">
              TreadAI is provided for informational and educational purposes only. By using this service, you acknowledge and agree to the following:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Not a Substitute for Professional Inspection:</strong> TreadAI's AI-powered tire analysis is not a replacement for professional tire inspection by a qualified mechanic or tire specialist.</li>
              <li><strong>No Liability:</strong> We are not responsible for any damage, injury, accidents, or other consequences that may result from relying on TreadAI's assessments.</li>
              <li><strong>Use at Your Own Risk:</strong> All tire condition assessments, tread depth measurements, and safety recommendations are estimates based on AI analysis and should be verified by a professional.</li>
              <li><strong>No Warranty:</strong> We make no warranties or guarantees regarding the accuracy, reliability, or completeness of the information provided by TreadAI.</li>
            </ul>
          </section>

          {/* Terms of Service */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p>
              By accessing or using TreadAI (the "Service"), including our website (tread-ai.com) and mobile application, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
            <p>
              TreadAI provides AI-powered tire condition analysis using computer vision and machine learning. The Service analyzes uploaded tire images to estimate:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Tire tread depth</li>
              <li>Tire condition (good, worn, or damaged)</li>
              <li>General safety recommendations</li>
            </ul>
            <p className="mt-4">
              <strong>These assessments are estimates only and should not be used as the sole basis for vehicle safety decisions.</strong>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, TreadAI, its owners, operators, developers, and affiliates shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages, including but not limited to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Personal injury or death</li>
              <li>Property damage</li>
              <li>Vehicle accidents or collisions</li>
              <li>Tire failure or blowouts</li>
              <li>Loss of use of vehicle</li>
              <li>Financial losses</li>
              <li>Any other damages arising from use of the Service</li>
            </ul>
            <p className="mt-4 font-semibold">
              YOU ASSUME ALL RISK ASSOCIATED WITH THE USE OF THIS SERVICE.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Professional Verification Required</h2>
            <p>
              Users are strongly advised to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Have tires professionally inspected by a certified mechanic</li>
              <li>Follow manufacturer recommendations for tire maintenance</li>
              <li>Comply with local regulations regarding tire safety</li>
              <li>Replace tires based on professional advice, not solely on TreadAI assessments</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. No Professional Relationship</h2>
            <p>
              Use of TreadAI does not create any professional relationship between you and TreadAI. We are not acting as your mechanic, tire specialist, or safety consultant.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Accuracy and Reliability</h2>
            <p>
              While we strive to provide accurate assessments, TreadAI's AI analysis may produce errors or inaccuracies due to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Poor image quality or lighting</li>
              <li>Unusual tire conditions or patterns</li>
              <li>Limitations of AI/ML technology</li>
              <li>Incomplete or missing data</li>
            </ul>
            <p className="mt-4">
              <strong>Always verify results with professional inspection before making safety-critical decisions.</strong>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. User Responsibilities</h2>
            <p>
              You are responsible for:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Maintaining safe tires on your vehicle</li>
              <li>Following all applicable traffic and safety laws</li>
              <li>Getting professional tire inspections regularly</li>
              <li>Using TreadAI as a supplementary tool only</li>
              <li>Not relying solely on TreadAI for tire safety decisions</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Data Privacy</h2>
            <p>
              Uploaded tire images may be stored for service improvement purposes. We do not sell your personal data. See our <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a> for details.
            </p>
          </section>

          {/* Subscription Terms — required by Apple App Review for IAP */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8a. Subscriptions and In-App Purchases (iOS)</h2>
            <p>
              The TreadAI iOS app offers in-app purchases for additional tire inspections beyond the free first inspection. All purchases are processed by Apple through the App Store and are subject to Apple's <a href="https://www.apple.com/legal/internet-services/itunes/" className="text-blue-600 underline">Media Services Terms and Conditions</a>.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Available products</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Pro Annual</strong> — $2.99 USD per year, auto-renewing subscription. Grants unlimited tire inspections for one year and renews automatically.</li>
            </ul>
            <p className="mt-4 text-sm text-gray-500">
              Prices may vary by region. The price shown in the App Store at the time of purchase is the price you will be charged.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Auto-renewal</h3>
            <p>
              The Pro Annual subscription automatically renews at the end of each one-year period unless cancelled at least <strong>24 hours before the end of the current period</strong>. Your Apple ID account will be charged for renewal within 24 hours prior to the end of the current period at the same price as the original subscription.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Managing or cancelling your subscription</h3>
            <p>
              You can manage your subscription and turn off auto-renewal at any time:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>On your iOS device: open <strong>Settings</strong> → tap your name at the top → <strong>Subscriptions</strong> → select <strong>TreadAI Pro</strong> → tap <strong>Cancel Subscription</strong></li>
              <li>From inside the TreadAI app: <strong>Settings → Your Plan → Manage Subscription</strong> (this opens the same Apple settings screen)</li>
              <li>Or visit <a href="https://apps.apple.com/account/subscriptions" className="text-blue-600 underline">https://apps.apple.com/account/subscriptions</a></li>
            </ul>
            <p className="mt-4">
              Cancellation takes effect at the end of the current billing period. You will continue to have access to Pro features until that date. Deleting the app does not cancel an active subscription.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Refunds</h3>
            <p>
              TreadAI does not directly process refunds. All refund requests for in-app purchases must be made through Apple. To request a refund, visit <a href="https://reportaproblem.apple.com" className="text-blue-600 underline">https://reportaproblem.apple.com</a> and sign in with your Apple ID. Apple's refund decisions are final.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Free first inspection</h3>
            <p>
              Each new installation of the TreadAI app on a new device receives one free tire inspection. After this free inspection has been used, additional inspections require an active Pro Annual subscription. The free inspection is tracked per device and is not transferable.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Restore purchases</h3>
            <p>
              Purchases are tied to your Apple ID. If you reinstall the app or install it on a new device signed in with the same Apple ID, you can recover your purchases by tapping <strong>Restore Purchases</strong> on the paywall or in <strong>Settings → Your Plan</strong>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Modifications to Service</h2>
            <p>
              We reserve the right to modify, suspend, or discontinue the Service at any time without notice or liability.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law provisions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless TreadAI and its affiliates from any claims, damages, losses, liabilities, and expenses arising from your use of the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Information</h2>
            <p>
              For questions about these Terms of Service, please contact us through our website.
            </p>
          </section>

          {/* Final Warning */}
          <section className="bg-red-50 border-l-4 border-red-500 p-6 my-8">
            <h2 className="text-xl font-bold text-red-900 mb-3">Final Reminder</h2>
            <p className="text-red-800 font-medium">
              TreadAI is a tool to assist with tire maintenance awareness. It is NOT a certified safety inspection service.
              Always consult with qualified professionals before making decisions that affect vehicle safety.
            </p>
          </section>
        </div>
      </div>
  );
};
